import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet-async';
import { passwordApi } from '../api/settingsApi';
import { PageHeader } from '../components/PageHeader';
import { useAuthStore } from '../store/authStore';

export const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const user = useAuthStore((s) => s.user);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const mutation = useMutation({
    mutationFn: ({ oldP, newP }: { oldP: string; newP: string }) =>
      passwordApi.changePassword(oldP, newP),
    onSuccess: () => {
      enqueueSnackbar('Heslo bylo změněno', { variant: 'success' });
      goBack();
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const goBack = () => {
    // Vzdy bezpecny redirect na dashboard role — nikdy ne navigate(-1) (mohla by byt prazdna historie)
    const target =
      user?.role === 'SUPER_ADMIN'
        ? '/superadmin'
        : user?.role === 'UNIT_ADMIN'
          ? '/admin'
          : '/recorder';
    navigate(target);
  };

  const passwordsMatch = newPassword === confirm;
  const valid = oldPassword && newPassword.length >= 8 && passwordsMatch;

  return (
    <>
      <Helmet>
        <title>Změna hesla</title>
      </Helmet>
      <PageHeader title="Změna hesla" subtitle="Bezpečnost vašeho účtu" />
      <Box sx={{ maxWidth: 520 }}>
        <Card>
          <CardContent>
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                if (valid) mutation.mutate({ oldP: oldPassword, newP: newPassword });
              }}
            >
              <Stack spacing={2}>
                <TextField
                  label="Současné heslo"
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  required
                  autoFocus
                  fullWidth
                  autoComplete="current-password"
                />
                <TextField
                  label="Nové heslo"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  helperText="Alespoň 8 znaků"
                  required
                  fullWidth
                  autoComplete="new-password"
                />
                <TextField
                  label="Potvrzení nového hesla"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  error={!!confirm && !passwordsMatch}
                  helperText={!!confirm && !passwordsMatch ? 'Hesla se neshodují' : ''}
                  required
                  fullWidth
                  autoComplete="new-password"
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button onClick={goBack}>Zrušit</Button>
                  <Button type="submit" variant="contained" disabled={!valid || mutation.isPending}>
                    {mutation.isPending ? 'Měním…' : 'Změnit heslo'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};
